import { useEffect, useMemo, useRef, useState } from "react";
import { Ico } from "./Icons";
import AllContentView from "./AllContentView";
import CollectionView from "./CollectionView";
import EmptyState from "./EmptyState";
import { useSnapshot } from "valtio";
import { contentCatalogStore } from "../contentCatalogStore";
import { ContentCatalogFolder } from "@models/contentCatalogFolder.model";
import { ContentCatalogService } from "@services/contentCatalog/ContentCatalogService";
import { Ad } from "@models/Ad.model";
import AdCard from "@/shared/components/AdCard";
import type { AdMenuItemsFn, FolderMenuItemsFn } from "../contentCatalog";
import { useAdProvenance } from "../useAdProvenance";

function findNodeById(nodes: ContentCatalogFolder[], id: number): ContentCatalogFolder | null {
    for (const node of nodes) {
        if (node.id === id) return node;
        if (node.children?.length) {
            const found = findNodeById(node.children, id);
            if (found) return found;
        }
    }
    return null;
}

function getSelectionContext(
    selectedId: string,
    collections: ContentCatalogFolder[],
    folders: ContentCatalogFolder[]
) {
    if (!selectedId) return null;
    if (selectedId === "__collections__") return { type: "collections-root" as const };
    if (selectedId === "__all__") return { type: "all-root" as const };

    const numId = Number(selectedId);
    if (isNaN(numId)) return null;

    const collectionNode = findNodeById(collections, numId);
    if (collectionNode) return { type: "collection" as const, node: collectionNode };

    const folderNode = findNodeById(folders, numId);
    if (folderNode) return { type: "folder" as const, node: folderNode };

    return null;
}

interface CatalogMainProps {
    /**
     * The catalog on screen (MPN-1579). Sent with the filter search so a client catalog's
     * own ads are searched, not just the shared templates. Not a permission -- the server
     * still scopes results to the caller's organizations.
     */
    organizationId: number;
    adMenuItems?: AdMenuItemsFn;
    collectionAdMenuItems?: AdMenuItemsFn;
    folderMenuItems?: FolderMenuItemsFn;
    collectionMenuItems?: FolderMenuItemsFn;
    onAdClick?: (ad: Ad) => void;
    enableDnd?: boolean;
}

export default function CatalogMain({
    organizationId,
    adMenuItems,
    collectionAdMenuItems,
    folderMenuItems,
    collectionMenuItems,
    onAdClick,
    enableDnd,
}: CatalogMainProps) {
    const { selectedId, selectedTagIds, panelMode, query, loading, tagGroups, filterResults, filterLoading, collections, folders } = useSnapshot(contentCatalogStore)
    // Which template each result was rebranded from. Answered per viewer by the server, so
    // a client-side role holding Content Catalog gets nothing back (MPN-1508).
    const templateNames = useAdProvenance(filterResults.map((ad) => (ad as Ad).id))

    const [view, setView] = useState("grid");
    const ccService = useMemo(() => new ContentCatalogService(), []);
    const ctx = getSelectionContext(selectedId, collections as ContentCatalogFolder[], folders as ContentCatalogFolder[]);
    const searchAbortRef = useRef<AbortController | null>(null);

    const handleTogglePanel = () => {
        contentCatalogStore.panelMode = panelMode === "browse" ? "filter" : "browse"
    };

    const handleRemoveTag = (id: number) => {
        contentCatalogStore.selectedTagIds = contentCatalogStore.selectedTagIds.filter((t) => t !== id);
    };

    const isFilterMode = panelMode === "filter";
    const hasFilters = selectedTagIds.length > 0;
    const hasQuery = query.trim().length > 0;
    const isCollectionCtx = ctx?.type === "collections-root" || ctx?.type === "collection";

    // Search API call when in filter mode
    useEffect(() => {
        if (!isFilterMode) return;
        if (!hasFilters && !hasQuery) {
            contentCatalogStore.filterResults = [];
            return;
        }

        const timeout = setTimeout(async () => {
            searchAbortRef.current?.abort();
            const controller = new AbortController();
            searchAbortRef.current = controller;

            contentCatalogStore.filterLoading = true;
            try {
                // Group selectedTagIds by tag group
                const tagsMap: Record<number, number[]> = {};
                for (const group of tagGroups) {
                    const matching = group.tags
                        .filter((t) => selectedTagIds.includes(t.id))
                        .map((t) => t.id);
                    if (matching.length > 0) {
                        tagsMap[group.id] = matching;
                    }
                }

                // Without the organization this searches templates only, which is what
                // made a client catalog's migrated content unfindable by tag (MPN-1579).
                const payload: Record<string, unknown> = { organization_id: organizationId };
                if (hasQuery) payload.search_query = query.trim();
                if (hasFilters) payload.filters = { tags: tagsMap };

                const results = await ccService.searchContent(payload);
                if (controller.signal.aborted) return;

                const ads = (results || []).map((r: any) => new Ad({ id: r.id, name: r.name }));
                contentCatalogStore.filterResults = ads;
            } catch (error) {
                if (!controller.signal.aborted) {
                    console.error("Filter search failed:", error);
                }
            } finally {
                if (!controller.signal.aborted) {
                    contentCatalogStore.filterLoading = false;
                }
            }
        }, 300);

        return () => clearTimeout(timeout);
    }, [isFilterMode, selectedTagIds, query, hasFilters, hasQuery, tagGroups, ccService, organizationId]);

    const renderContent = () => {
        if (isFilterMode) {
            if (filterLoading) {
                return (
                    <div className="content-area">
                        <span className="catalog-spinner" />
                    </div>
                );
            }

            if (!hasFilters && !hasQuery) {
                return (
                    <div className="content-area">
                        <p className="filter-results-label">
                            Select filters in the left panel or search to narrow results.
                        </p>
                    </div>
                );
            }

            if (filterResults.length === 0) {
                return (
                    <div className="content-area">
                        <p className="filter-results-label">No ads match the current filters.</p>
                    </div>
                );
            }

            return (
                <div className="content-area">
                    <section className="content-section">
                        <div className="content-section-header">
                            <span className="content-section-title">Results</span>
                            <span className="content-section-count">{filterResults.length}</span>
                        </div>
                        <div className={`ad-grid${view === "list" ? " ad-grid--list" : ""}`}>
                            {filterResults.map((ad) => {
                                const card = (
                                    <AdCard
                                        key={ad.id}
                                        ad={ad as Ad}
                                        mode="detail"
                                        sourceTemplateName={templateNames[(ad as Ad).id]}
                                        onClick={() => onAdClick?.(ad as Ad)}
                                    />
                                );
                                return view === "list"
                                    ? <div key={(ad as Ad).id} className="cc-adcard-list-wrapper">{card}</div>
                                    : card;
                            })}
                        </div>
                    </section>
                </div>
            );
        }

        if (!ctx) return <EmptyState message="Select something from the sidebar" />;
        if (isCollectionCtx) return (
            <CollectionView
                node={ctx.type === "collection" ? ctx.node : null}
                onAdClick={onAdClick}
                adMenuItems={collectionAdMenuItems}
                folderMenuItems={collectionMenuItems}
                enableDnd={enableDnd}
            />
        );
        return (
            <AllContentView
                node={ctx.type === "all-root" ? null : ctx.node}
                view={view}
                onAdClick={onAdClick}
                adMenuItems={adMenuItems}
                folderMenuItems={folderMenuItems}
                enableDnd={enableDnd}
            />
        );
    };

    return (
        <div className="catalog-main">

            <div className="catalog-topbar">
                <div className="catalog-search-wrap">
                    <span className="catalog-search-icon"><Ico.Search /></span>
                    <input
                        className="catalog-search"
                        type="text"
                        placeholder="Search folders and ads..."
                        value={query}
                        onChange={(e) => contentCatalogStore.query = e.target.value}
                    />
                    {query && !loading && (
                        <button className="catalog-search-clear" onClick={() => contentCatalogStore.query = ""}><Ico.X /></button>
                    )}
                    {loading && <span className="catalog-spinner" />}
                </div>

                <div className="catalog-topbar-right">
                    <button
                        className={[
                            "filter-toggle-btn",
                            isFilterMode ? "filter-toggle-btn--active" : "",
                            hasFilters && !isFilterMode ? "filter-toggle-btn--has-filters" : "",
                        ].join(" ").trim()}
                        onClick={handleTogglePanel}
                        title={isFilterMode ? "Switch to browse" : "Filter by tags"}
                    >
                        <Ico.Filter />
                        Filters
                        {hasFilters && !isFilterMode && (
                            <span className="filter-toggle-badge">{selectedTagIds.length}</span>
                        )}
                    </button>

                    {(isFilterMode || !isCollectionCtx) && (
                        <div className="catalog-view-toggle">
                            <button className={view === "grid" ? "active" : ""} onClick={() => setView("grid")} title="Grid view"><Ico.Grid /></button>
                            <button className={view === "list" ? "active" : ""} onClick={() => setView("list")} title="List view"><Ico.List /></button>
                        </div>
                    )}
                </div>
            </div>


            {isFilterMode && hasFilters && (
                <div className="active-chips">
                    {selectedTagIds.map((id) => {
                        const label = tagGroups.flatMap((g) => g.tags).find((t) => t.id === id)?.name || id;
                        return (
                            <span key={id} className="active-chip">
                                {label}
                                <button onClick={() => handleRemoveTag(id)}><Ico.X size={8} /></button>
                            </span>
                        );
                    })}
                </div>
            )}


            <div className="catalog-body-scroll">
                {renderContent()}
            </div>
        </div>
    );
}
